/* eslint-enable jsdoc/require-throws */
import { GetSellerInputQuery_reception_getSellerInput_sellerInput_partnershipReferrals } from 'src/api/queries/__generated__/GetSellerInputQuery';
import { PartnershipReferral, Product } from 'src/declarations';
import { EvaluatedFlowPage, evaluatePage, FlowPageSet } from 'src/lib/flow/FlowPage';
import { ExperimentFlags } from 'src/store/experimentFlags';
import { FeatureFlags } from 'src/store/featureFlags';
import { AnswerSet } from 'src/store/sellerFlow/AnswerSet';
import { AnswerPrefill } from 'src/store/sellerInput';

import { FlowQuestion } from './FlowQuestion';
import { getPageLoaderSlugs } from './loaders';

class FlowError extends Error {
  // See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error#custom_error_types
  constructor(message: string) {
    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super(message);

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, FlowError);
    }

    this.name = 'FlowError';
  }
}

/**
 * This holds information we've collected from the user, the address, and any
 * other interesting data needed to make decisions about the flow
 *
 * As the user goes through the flow `answers` a `experimentFlags` may get
 * updated. The rest of this field is static after we've loaded the seller
 * input.
 */
export type FlowContext = {
  answers: AnswerSet;
  inputContext: {
    // details about the seller input
    //should be static
    answerPrefills: Record<string, AnswerPrefill>;
    sellerInputUUID: string;
    channel: string;
    marketDisplayName: string;
    market: string;
    isInBwODMarket: boolean;
    isHomebuilderLead: boolean;
    homebuilderIdentifier?: string;
    hasHomebuilderSalesAssociate?: boolean;
    inApp: boolean;
    orePartnerIdentifier?: string;
    isInPartnershipExpansionDenial: boolean;
    homeValuationEstimate?: {
      min: number;
      max: number;
      variance: number;
    };
    eligibleProducts: Array<Product>;
    partnerIdentifier?: string;
    partnerships: Array<
      | GetSellerInputQuery_reception_getSellerInput_sellerInput_partnershipReferrals
      | PartnershipReferral
    >;
    sellerFlowName: string;
    // Question keys that have low confidence or are initially invalid
    lowConfidenceQuestions?: Array<string>;
  };
  customer: {
    // might change during app usage?
    customerUUID?: string;
    // these depend on answers - so maybe don't belong here
    yourOrYourClients: string;
    yourOrYourClient: string;
  };
  featureFlags: FeatureFlags;
  // may change during app usage
  experimentFlags: ExperimentFlags;
};

export type FlowState = {
  index: number;
  lastCompletedIndex: number;
  atEnd: boolean;
  exitPath: string | null;
  atStart: boolean;
  pageSet: FlowPageSet;
  ui: FlowUiState;
};

export type FlowUiState = {
  progress: { current: number; total: number; enabledPages: Array<string> };
  page: EvaluatedFlowPage;
  previousPage: EvaluatedFlowPage | null;
  pageIndex: number;
  canAdvance: boolean;
  // This is true when the ui is allowed to auto submit and request the next
  // page, false otherwise
  isReadyForAutoSubmit: boolean;
  // This is true when the page is eligible for auto submission - that is it
  // does not already have answers
  canAutoAdvance: boolean;
  // some loading indicator states here too?
};

//Loop through a questions array, and find any followups
export const getQuestionKeys = (
  questions: FlowQuestion[],
  filterFn?: (q: FlowQuestion) => boolean,
): Array<string> => {
  const allKeys = questions.reduce((keys: Array<string>, question): Array<string> => {
    if (!filterFn || filterFn(question)) {
      keys = keys.concat(question.key);
    }

    // if question has options, check and see if those options have follow ups
    // and get those keys too
    if (
      (question.type == 'multi_select' || question.type == 'radio_single_select') &&
      'options' in question &&
      question.options
    ) {
      keys = keys.concat(
        question.options.reduce((optionKeys: Array<string>, option): Array<string> => {
          if ('followUps' in option && option.followUps) {
            optionKeys = optionKeys.concat(getQuestionKeys(option.followUps));
          }
          return optionKeys;
        }, []),
      );
    }
    return keys;
  }, [] as Array<string>);

  return allKeys;
};

/**
 * Return the keys used in the answer set passed in on the secondaryAction page config
 * This is used when calculating the page submit, we will know to also send these values
 */
const getSecondaryActionKeys = (config: EvaluatedFlowPage['secondaryAction']): Array<string> => {
  if (config) {
    return config.answerSet.map(({ key }) => key);
  }
  return [];
};

/**
 *  This function will recursive go through our question tree and a check on all active question fields.
 *  Active in this case means that we're showing the user the question, so the top level array of
 *  questions from a page are all active. Additionally, if a question has followUps
 *  and we are showing them (because the current answer set includes that option selected)
 *  we will check those follow ups as well.
 *
 *  This function is the heart of:
 *  areAllRequiredQuestionsAnswered
 *  isReadyForAutoSubmit
 */
export const checkActiveQuestions = (
  questions: FlowQuestion[],
  answers: AnswerSet,
  checkFn: (question: FlowQuestion, answer: AnswerSet[string]) => boolean,
): boolean => {
  return questions.every((question) => {
    const answer = answers[question.key];
    if (!checkFn(question, answer)) {
      return false;
    }

    // if this question has follow ups, let's see if those pass
    if (
      answer &&
      (question.type == 'multi_select' || question.type == 'radio_single_select') &&
      'options' in question &&
      question.options
    ) {
      // make sure our multi_select has an actual answer before proceeding
      if (question.type == 'multi_select') {
        if (Object.values(answer).every((a) => a == false || a == null || a == undefined)) {
          // we have no answers, it's okay if the question is optional, otherwise
          // it is not okay.
          return !!question.optional;
        }
      }

      return question.options.every((option) => {
        if (
          (option.value == answer || (typeof option.value === 'string' && answer[option.value])) &&
          option.followUps
        ) {
          return checkActiveQuestions(option.followUps, answers, checkFn);
        }
        // This handles two cases:
        // 1. option has no follow ups, but is selected
        // 2. option is not selected (follow ups not enabled)
        //
        // For both of these we know the overall question passes the checkFn,
        // so here we can return true
        return true;
      });
    }

    return true;
  });
};

const getIsReadyForAutoSubmit = (questions: FlowQuestion[], answers: AnswerSet): boolean =>
  checkActiveQuestions(
    questions,
    answers,
    (question: FlowQuestion, answer: AnswerSet[string]): boolean => {
      // if we have a multi_select question
      // enable auto advance if the user picks the _none_ option
      if (question.type == 'multi_select') {
        // if a checkbox only has one option and it is selected, then we also can auto advance
        if (question.options.length == 1) {
          return typeof answer !== 'undefined' && answer !== null;
        }

        // else if the none option is selected we can auto advance as well
        return answer == '_none_' || answer?.['_none_'] === true;
      }

      // types where we enable auto advancing
      if (
        ['radio_single_select', 'image_single_select', 'exclusives_radio_single_select'].includes(
          question.type,
        )
      ) {
        return typeof answer !== 'undefined' && answer !== null;
      }

      // we have a question type that requires more interactive user input (ie
      // user needs to type into textbox) Potentially we could change this if
      // we only updated answers onBlur instead of on every keystroke
      return false;
    },
  );

const isReadyForAutoSubmit = (page: EvaluatedFlowPage, answers: AnswerSet): boolean => {
  if (!page.autoAdvance) {
    return false;
  }
  return getIsReadyForAutoSubmit(page.questions, answers);
};

// can this page be automatically advanced to next page
const getCanAutoAdvance = (page: EvaluatedFlowPage, answers: AnswerSet): boolean => {
  if (!page.autoAdvance) {
    return false;
  }

  let keys: Array<string> = [];
  if (page.questions) {
    keys = getQuestionKeys(page.questions);
  }
  if (keys.length == 0) {
    // eslint-disable-next-line no-console
    console.error('FlowPage can autoAdvance, but there are no questions found.');
  }

  const hasAnswers = keys.some((key) => {
    return typeof answers[key] !== 'undefined' && answers[key] !== null;
  });
  // we only enable auto advance when we visit a page and have no answers for it
  return !hasAnswers;
};

const isQuestionAnswered = (question: FlowQuestion, answer: AnswerSet[string]): boolean => {
  // TODO: find a way to not need this
  // numeric dropdown_select come in with a default answer of `0` - so we have no way of knowing
  // if the user has made a selection. If the answer falls in the min and max
  // range then we can assume/hope 0 is not included and a passing check here
  // means they selected a value
  if (
    typeof answer !== 'undefined' &&
    question.type == 'dropdown_select' &&
    question.minValue &&
    question.maxValue
  ) {
    // TODO: we only render < max_value?
    return question.minValue <= answer && question.maxValue > answer;
  }
  // default to checking for presence of a value
  return typeof answer !== 'undefined' && answer !== null && answer !== '';
};

const areAllRequiredQuestionsAnswered = (questions: FlowQuestion[], answers: AnswerSet): boolean =>
  checkActiveQuestions(
    questions,
    answers,
    (question: FlowQuestion, answer: AnswerSet[string]): boolean => {
      if (question.type == 'element' || question.optional || question.hidden) {
        return true;
      }

      return isQuestionAnswered(question, answer);
    },
  );

// requirements for page to advance have been met
const getCanAdvance = (page: EvaluatedFlowPage, answers: AnswerSet): boolean => {
  const allRequiredAnswered = areAllRequiredQuestionsAnswered(page.questions, answers);
  return allRequiredAnswered;
};

const getProgress = (pageSet: FlowPageSet, currentPageIndex: number, flowContext: FlowContext) => {
  return pageSet.reduce(
    ({ total, current, enabledPages }, page, idx) => {
      if (idx == currentPageIndex) {
        current = total + 1;
      }
      if (page.isEnabled(flowContext)) {
        enabledPages.push(page.slug());
        return { total: total + 1, current, enabledPages };
      }
      return { total, current, enabledPages };
    },
    { total: 0, current: 0, enabledPages: [] as Array<string> },
  );
};

// probably needs to come out of fs, since I think all of the above functions will also want to update the ui state to the latest
// const fs = getUiState(fs)
const getUiState = (
  pageSet: FlowPageSet,
  pageIndex: number,
  atEnd: boolean,
  flowContext: FlowContext,
  previousUiState?: FlowUiState,
): FlowUiState => {
  let page: EvaluatedFlowPage;
  let canAutoAdvance: boolean;

  // canAutoAdvance needs to be calculated only once when we first evaluate the
  // page, so here we check if our index has changed and if not we re-use page
  // and canAutoAdvance
  if (previousUiState && pageIndex == previousUiState.pageIndex) {
    page = {
      ...previousUiState.page,
      // questions needs to be updated on answer changes
      questions: pageSet[pageIndex].questions(flowContext),
    };
    canAutoAdvance = previousUiState.canAutoAdvance;
  } else {
    page = evaluatePage(pageSet[pageIndex], flowContext);
    canAutoAdvance = getCanAutoAdvance(page, flowContext.answers);
  }

  const { page: previousPage } = getPreviousEnabledPage(pageIndex, pageSet, flowContext);
  const progress = getProgress(pageSet, pageIndex, flowContext);

  return {
    progress,
    page,
    previousPage,
    pageIndex,
    canAdvance: getCanAdvance(page, flowContext.answers),
    isReadyForAutoSubmit:
      !atEnd && canAutoAdvance && isReadyForAutoSubmit(page, flowContext.answers),
    canAutoAdvance,
  };
};

/**
 * This will return an updated flowState given a current state and context and
 * a slug
 *
 * @throws If the slug is not found in the page set an exception is thrown
 */
export const getStateFromSlug = (
  pageSet: FlowPageSet,
  flowContext: FlowContext,
  slug: string,
  lastCompletedIndex: number,
): FlowState => {
  let index = pageSet.findIndex((page) => page.slug() == slug);

  // walk backwards if the user visits a page that is not enabled
  if (index !== -1 && !pageSet[index].isEnabled(flowContext)) {
    //eslint-disable-next-line no-console
    console.info(`Requested slug: ${slug} is not enabled, attempting to find an earlier page.`);
    while (index >= 0 && !pageSet[index].isEnabled(flowContext)) {
      index--;
    }
  }

  if (index !== -1) {
    const { page: nextPage } = getNextEnabledPage(index, pageSet, flowContext);
    const { page: previousPage } = getPreviousEnabledPage(index, pageSet, flowContext);

    return {
      index,
      lastCompletedIndex,
      atEnd: !nextPage,
      exitPath: null,
      atStart: !previousPage,
      pageSet: pageSet,
      ui: getUiState(pageSet, index, !nextPage, flowContext),
    };
  }

  //TODO - fix page set name here
  throw new FlowError(
    `Could not find requested page or it is not enabled in flow: ${slug} in page set.`,
  );
};

export const createNewFlowState = (
  pageSet: FlowPageSet,
  flowContext: FlowContext,
  lastCompletedIndex = -1,
): FlowState => {
  return {
    // These will be corrected when we call nextPage
    index: -1,
    lastCompletedIndex: lastCompletedIndex,
    atEnd: false,
    exitPath: null,
    atStart: false,
    pageSet: pageSet,
    ui: getUiState(pageSet, 0, false, flowContext),
  };
};

// disable throws requirement for the next docblock since only throw unhandled exceptions
/* eslint-disable jsdoc/require-throws */
/**
 * Creates the initial flow state, attempting to load the current page from the
 * our location.href. If a page cannot be found from the href then this will
 * initialize to the first enabled page in the PageSet
 */
export const initFlowState = (
  pageSet: FlowPageSet,
  flowContext: FlowContext,
  slug?: string,
  lastCompletedSlug?: string,
): FlowState => {
  // figure out what the last page completed was
  let lastCompletedIndex = -1;
  if (lastCompletedSlug) {
    lastCompletedIndex = pageSet.findIndex((page) => page.slug() == lastCompletedSlug);
  }

  let bootstrap: FlowState | undefined = undefined;
  if (slug) {
    try {
      bootstrap = getStateFromSlug(pageSet, flowContext, slug, lastCompletedIndex);
    } catch (err: unknown) {
      // if we could not find the page in pathname, let's log it and continue
      // using the start of the flow
      if (err instanceof FlowError) {
        /* eslint-disable no-console */
        console.info(`Could not find page for ${slug} , falling back to start of flow`);
        console.info(err.message);
        /* eslint-enable no-console */
      } else {
        // anything else we cannot handle, so toss it up
        throw err;
      }
    }
  }
  if (typeof bootstrap == 'undefined') {
    //const index = getPageIndexFromLocation(pageSet, flowContext, location.pathname ?? '');
    bootstrap = createNewFlowState(pageSet, flowContext, lastCompletedIndex);

    const alwaysReturnPagesWithLoaders = getPageLoaderSlugs();
    const firstPageState = nextPage(bootstrap, flowContext, alwaysReturnPagesWithLoaders);
    if (firstPageState == null) {
      // eslint-disable-next-line no-console
      console.info('No pages are enabled, doing default exit');
      bootstrap.atEnd = true;
    } else {
      bootstrap = firstPageState;
    }
  }

  return bootstrap;
};

/**
 * Creates a new updated flow state given the current flow state and an updated
 * flow context
 */
export const getUpdatedFlowState = (
  currentFlowState: FlowState,
  flowContext: FlowContext,
): FlowState => {
  const { page: nextPage } = getNextEnabledPage(
    currentFlowState.index,
    currentFlowState.pageSet,
    flowContext,
  );

  return {
    ...currentFlowState,
    atEnd: !nextPage,
    ui: getUiState(
      currentFlowState.pageSet,
      currentFlowState.index,
      !nextPage,
      flowContext,
      currentFlowState.ui,
    ),
  };
};

const getAdjacentEnabledPage =
  (direction: 'next' | 'previous') =>
  (
    index: number,
    pageSet: FlowPageSet,
    flowContext: FlowContext,
    /**
     * For pages that need experiment flags loaded before calling isEnabled we
     * can pass them in here and have them be returned without ever checking
     * isEnabled
     */
    overrideIsEnabledPages: Array<string> = [],
  ): { index: number; page: EvaluatedFlowPage | null } => {
    const dir = direction == 'next' ? 1 : -1;

    let newIndex = index + dir;
    while (pageSet[newIndex] && newIndex >= 0 && newIndex < pageSet.length) {
      const page = evaluatePage(pageSet[newIndex], flowContext);

      if (page.isEnabled || overrideIsEnabledPages.includes(page.slug)) {
        return { index: newIndex, page };
      }
      newIndex += dir;
    }

    // no adjacent page found
    return { index: -1, page: null };
  };

const getNextEnabledPage = getAdjacentEnabledPage('next');
const getPreviousEnabledPage = getAdjacentEnabledPage('previous');

/**
 * @returns FlowState when an enabled next page can be found, otherwise it will return null
 */
export const nextPage = (
  currentFlowState: FlowState,
  flowContext: FlowContext,
  overrideIsEnabledPages: Array<string> = [],
): FlowState | null => {
  const currentPage = currentFlowState.pageSet[currentFlowState.index];
  if (currentPage?.exitPath) {
    const exitPath = currentPage.exitPath(flowContext);
    if (exitPath) {
      return getUpdatedFlowState(
        {
          ...currentFlowState,
          atEnd: true,
          exitPath,
        },
        flowContext,
      );
    }
  }

  const { index: nextIndex, page } = getNextEnabledPage(
    currentFlowState.index,
    currentFlowState.pageSet,
    flowContext,
    overrideIsEnabledPages,
  );

  if (page) {
    const { index: nextNextIndex } = getNextEnabledPage(
      nextIndex,
      currentFlowState.pageSet,
      flowContext,
      overrideIsEnabledPages,
    );
    return getUpdatedFlowState(
      {
        ...currentFlowState,
        index: nextIndex,
        atEnd: nextNextIndex == -1,
        atStart: currentFlowState.index == -1,
      },
      flowContext,
    );
  }

  return null;
};

/**
 * This returns an updated flow state where the page is set to the previous enabled page
 * @returns FlowState when an enabled previous page can be found, otherwise it will return null
 *
 */
export const previousPage = (
  currentFlowState: FlowState,
  flowContext: FlowContext,
): FlowState | null => {
  const { index: previousIndex, page } = getPreviousEnabledPage(
    currentFlowState.index,
    currentFlowState.pageSet,
    flowContext,
  );

  if (page) {
    //Check if we're at the start already
    const { index: nextPreviousIndex } = getPreviousEnabledPage(
      previousIndex,
      currentFlowState.pageSet,
      flowContext,
    );

    return getUpdatedFlowState(
      {
        ...currentFlowState,
        index: previousIndex,
        atEnd: false,
        atStart: nextPreviousIndex == -1,
      },
      flowContext,
    );
  }

  return null;
};

/* Currently unused:
export const getPreviousPage = (
  currentFlowState: FlowState,
  flowContext: FlowContext,
): EvaluatedFlowPage | null => {
  const { page } = getPreviousEnabledPage(
    currentFlowState.index,
    currentFlowState.pageSet,
    flowContext,
  );
  return page;
};
*/

/**
 * @returns A map of question keys to either null or the answer provided by the user
 */
export const getPageSubmit = (state: FlowState, ctx: FlowContext, changedAnswers: AnswerSet) => {
  // get answers for current page, and clear any removed answers for current page
  const keys = getQuestionKeys(state.ui.page.questions).concat(
    getSecondaryActionKeys(state.ui.page.secondaryAction),
  );
  const submittedAnswers = keys.reduce((answers, key) => {
    if (typeof changedAnswers[key] !== 'undefined') {
      answers[key] = changedAnswers[key];
    } else if (ctx.answers[key]) {
      // clear out answers that had an answer but no longer have one
      answers[key] = null;
    }
    return answers;
  }, {} as Record<string, AnswerSet[string] | null>);

  // get all keys that have been answered on previous pages
  // * ignore if the previous answer is now hidden
  // * also if a question somehow appears twice in the flow this will avoid
  //   clearing it out *except* if it is empty and we're submitting on of the
  //   pages it appears on
  const previousPages = state.pageSet.slice(0, state.index);

  // get answerd on earlier pages, initialized with keys from our current page
  const answeredOnEarlierPageKeys = new Set(
    previousPages.reduce((allKeys, page) => {
      const keys = getQuestionKeys(page.questions(ctx), (q: FlowQuestion) => {
        // return only questions that already have an answer
        if (ctx.answers[q.key]) {
          return true;
        }
        return false;
      });
      return allKeys.concat(keys);
    }, keys as Array<string>),
  );

  // we use this to store any question key that is currently enabled. This is
  // then used to filter the hiddenQuestionKeys since sometimes a question can
  // be on different pages but only shown once (or maybe twice? why not...). We
  // want to make sure that if we eventually/potentially show the question we
  // do not clear it
  const visibleQuestionKeysSet = new Set();

  // find additional questions that now need to be cleared because our answers
  // have changed - this is relevant when the user has gone backwards in the
  // flow collect all visible question keys and also all hidden question keys
  // on future pages
  let hiddenQuestionKeys: Array<string> = [];
  const futurePages = state.pageSet.slice(state.index + 1);
  futurePages.forEach((page) => {
    // skip our curent page, we've already handled it above
    if (page.slug() == state.ui.page.slug) {
      return;
    }

    // find keys on other pages that we need to clear because the entire page
    // or the question has become hidden possibly based on our new answers
    let hiddenKeys: Array<string> = [];
    if (page.isEnabled(ctx)) {
      // find keys that are hidden or keep_answer_if_hidden is false
      hiddenKeys = getQuestionKeys(page.questions(ctx), (q: FlowQuestion) => {
        // question is visible, or we need to keep the answer
        //
        if (q.type !== 'element' && (!q.hidden || q.keepAnswerIfHidden)) {
          visibleQuestionKeysSet.add(q.key);
          return false;
        }

        // this is a hidden question
        return true;
      });
    } else {
      // page is not enabled, so remove all keys, but skip ones with
      // keep_answer_if_hidden
      hiddenKeys = getQuestionKeys(
        page.questions(ctx),
        (q: FlowQuestion) => q.type !== 'element' && !q.keepAnswerIfHidden,
      );
    }
    hiddenQuestionKeys = hiddenQuestionKeys.concat(hiddenKeys);
  });

  // These keys come from the account page currently and we want to make sure we do not clear them out
  // At the moment we will attempt to clear out seller.phone_number since it also shows up on a separate
  // page that is sometimes not enabled, since the AccountPage is a special case we are currently unable to
  // use the above functions to filter out these fields as ones to keep.
  const alwaysSaveKeys = new Set(['seller.email', 'seller.phone_number', 'seller.full_name']);

  new Set(hiddenQuestionKeys).forEach((k) => {
    // skip any question that is potentially enabled or if it was answered in
    // an earlier page
    if (
      visibleQuestionKeysSet.has(k) ||
      answeredOnEarlierPageKeys.has(k) ||
      alwaysSaveKeys.has(k)
    ) {
      return;
    }

    // only set null if we have an answer
    if (ctx.answers[k] !== undefined && ctx.answers[k] !== null) {
      submittedAnswers[k] = null;
    }
  });

  let completedSlug = '';
  if (state.index >= state.lastCompletedIndex) {
    completedSlug = state.ui.page.slug;
  }

  return {
    submittedAnswers,
    completedSlug,
  };
};
